/**
 * The cache: the rows the app shows with no signal, kept true by
 * `GET /api/changes` (unit 25, rules 5 to 7).
 *
 * Each row is in its area's own model (`TodoOut`, `HabitOut`, `ListOut`), as
 * the server sends it. A row with a write still queued is the person's latest
 * word on it, so a change from the server does not overwrite it: the write
 * lands, and the next change brings the server's row back (D4: the last write
 * to arrive wins).
 */

import type { Schemas } from "../api/client";
import { all, getMeta, transact } from "./idb";
import { dropImages } from "./images";
import type { Queued } from "./outbox";

export type Entity = Schemas["Deleted"]["entity"];

/** Each synced entity's row, in its area's model. The compiler holds this to
 * the contract's `Entity`: a new entity with no row type here fails `check`. */
export interface Rows {
  list: Schemas["ListOut"];
  todo: Schemas["TodoOut"];
  habit: Schemas["HabitOut"];
  journal: Schemas["JournalEntry"];
  note: Schemas["SyncedNote"];
  notebook: Schemas["Notebook"];
  meal: Schemas["PlanEntry"];
  recipe_cooked: Schemas["RecipeCooked"];
  concern: Schemas["SyncedConcern"];
  turner: Schemas["SyncedTurnerEntry"];
  turner_hidden: Schemas["HiddenEvent"];
  card: Schemas["Card"];
  step: Schemas["StretchStep"];
  account: Schemas["Account"];
  reading: Schemas["SyncedReading"];
}

/** Every entity has a row type, and no row type is for an entity that is not. */
type Exact<A, B> = [A] extends [B] ? ([B] extends [A] ? true : never) : never;
export const rowsCoverEveryEntity: Exact<keyof Rows, Entity> = true;

export interface Cached<E extends Entity = Entity> {
  entity: E;
  id: string;
  row: Rows[E];
}

export type Changes = Schemas["Changes"];

/**
 * Change a cached row in place, if it is cached — the effect of a write that
 * edits a row (`enqueue`'s `effect`), inside its transaction.
 */
export function edit<E extends Entity>(
  cache: IDBObjectStore,
  entity: E,
  id: string,
  change: (row: Rows[E]) => Rows[E],
): void {
  const read = cache.get([entity, id]);
  read.onsuccess = () => {
    const cached = read.result as Cached<E> | undefined;
    if (cached) cache.put({ ...cached, row: change(cached.row) });
  };
}

/**
 * Rule 7. The store holds one person's cache. Another person signing in drops
 * it — and the cursor with it — but leaves the first person's queued writes
 * where they are: they are theirs, and go when they sign in again. Returns
 * whether the cache was dropped, so the caller knows to fetch whole.
 */
export async function bindAccount(db: IDBDatabase, account: number): Promise<boolean> {
  const dropped = await transact<boolean>(db, ["cache", "meta"], "readwrite", (t, result) => {
    const meta = t.objectStore("meta");
    const read = meta.get("account");
    read.onsuccess = () => {
      result.value = read.result !== account;
      if (!result.value) return;
      t.objectStore("cache").clear();
      meta.delete("cursor");
      meta.put(account, "account");
    };
  });
  // The pictures the Service Worker kept are this person's too (25C7).
  if (dropped) await dropImages();
  return dropped;
}

export function boundAccount(db: IDBDatabase): Promise<number | undefined> {
  return getMeta<number>(db, "account");
}

export function cursor(db: IDBDatabase): Promise<number | undefined> {
  return getMeta<number>(db, "cursor");
}

/** Every cached row of one entity. */
export async function rows<E extends Entity>(db: IDBDatabase, entity: E): Promise<Rows[E][]> {
  const cached = await all<Cached>(db, "cache");
  return cached.filter((c): c is Cached<E> => c.entity === entity).map((c) => c.row);
}

export async function row<E extends Entity>(
  db: IDBDatabase,
  entity: E,
  id: string,
): Promise<Rows[E] | undefined> {
  return transact<Rows[E] | undefined>(db, ["cache"], "readonly", (t, result) => {
    const read = t.objectStore("cache").get([entity, id]);
    read.onsuccess = () => {
      result.value = (read.result as Cached<E> | undefined)?.row;
    };
  });
}

/**
 * The rows a queued write still holds, for one write: its own, and — for a
 * whole order set at once (`entityId: "order"`, a stretching reorder) —
 * each row it names, whose `position` it changed. 28B found a whole read
 * putting the old order back while a reorder waited to be sent.
 */
function held(w: Queued): string[] {
  const body = w.body as { ids?: unknown } | undefined;
  if (w.entityId === "order" && Array.isArray(body?.ids))
    return body.ids.map((id) => `${w.entity}:${String(id)}`);
  return [`${w.entity}:${w.entityId}`];
}

/** The entities with a write still queued for the bound person. */
function pending(t: IDBTransaction, done: (keys: Set<string>) => void): void {
  const meta = t.objectStore("meta").get("account");
  meta.onsuccess = () => {
    const writes = t.objectStore("outbox").getAll();
    writes.onsuccess = () => {
      done(
        new Set((writes.result as Queued[]).filter((w) => w.account === meta.result).flatMap(held)),
      );
    };
  };
}

/**
 * Rule 5 — an answer from `GET /api/changes`, applied. A reset drops the
 * cache and keeps the new cursor: the caller then fetches whole and `fill`s,
 * and a change made during that fetch comes again next time.
 */
export function apply(db: IDBDatabase, changes: Changes): Promise<void> {
  return transact<void>(db, ["cache", "meta", "outbox"], "readwrite", (t) => {
    const cache = t.objectStore("cache");
    t.objectStore("meta").put(changes.cursor, "cursor");
    pending(t, (mine) => {
      if (changes.reset) {
        clearExcept(cache, mine);
        return;
      }
      for (const change of changes.changes) {
        if (mine.has(`${change.entity}:${change.id}`)) continue;
        if (change.op === "delete") cache.delete([change.entity, change.id]);
        else cache.put({ entity: change.entity, id: change.id, row: change.row });
      }
    });
  });
}

/**
 * Drop every row (of `entity`, if named) but those in `keep`, then call
 * `then`. A row put before the walk ends is walked over too — so a caller
 * that puts rows does so in `then`, never beside the walk (27A's fix: `fill`
 * did, and a fill over rows already there kept none).
 */
function clearExcept(
  cache: IDBObjectStore,
  keep: Set<string>,
  entity?: Entity,
  then?: () => void,
): void {
  const walk = cache.openCursor();
  walk.onsuccess = () => {
    const at = walk.result;
    if (!at) {
      then?.();
      return;
    }
    const value = at.value as Cached;
    if (
      (entity === undefined || value.entity === entity) &&
      !keep.has(`${value.entity}:${value.id}`)
    )
      at.delete();
    at.continue();
  };
}

/**
 * A row's key in the cache: its `id`, as text (a concern's is an integer) —
 * but a recipe's cooking is keyed by its recipe, and a hidden event by
 * `source:event_id`, as `GET /api/changes` names them (25C3, 25C5).
 */
export function keyOf<E extends Entity>(entity: E, row: Rows[E]): string {
  if (entity === "recipe_cooked") return String((row as Rows["recipe_cooked"]).recipe_id);
  if (entity === "turner_hidden") {
    const hidden = row as Rows["turner_hidden"];
    return `${hidden.source}:${hidden.event_id}`;
  }
  return String((row as { id: string | number }).id);
}

/** One entity's rows, fetched whole after a reset. A row with a queued write stays as it is. */
export function fill<E extends Entity>(
  db: IDBDatabase,
  entity: E,
  fetched: Rows[E][],
): Promise<void> {
  return transact<void>(db, ["cache", "meta", "outbox"], "readwrite", (t) => {
    const cache = t.objectStore("cache");
    pending(t, (mine) => {
      clearExcept(cache, mine, entity, () => {
        for (const fresh of fetched) {
          const id = keyOf(entity, fresh);
          if (!mine.has(`${entity}:${id}`)) cache.put({ entity, id, row: fresh });
        }
      });
    });
  });
}

/**
 * Some of one entity's rows, as the server just answered them — a widget's
 * read of its day (27A). Each is put in the cache unless a write to it is
 * still queued; no other row is touched. Unlike `fill`, it removes nothing.
 */
export function merge<E extends Entity>(
  db: IDBDatabase,
  entity: E,
  fetched: Rows[E][],
): Promise<void> {
  return transact<void>(db, ["cache", "meta", "outbox"], "readwrite", (t) => {
    const cache = t.objectStore("cache");
    pending(t, (mine) => {
      for (const fresh of fetched) {
        const id = keyOf(entity, fresh);
        if (!mine.has(`${entity}:${id}`)) cache.put({ entity, id, row: fresh });
      }
    });
  });
}

/**
 * Ask what changed since the cursor, and apply it. Returns whether it was a
 * reset — the caller's cue to fetch whole — or `null` when the server could
 * not be asked.
 */
export async function refresh(
  db: IDBDatabase,
  fetcher: typeof fetch,
): Promise<{ reset: boolean } | null> {
  const since = await cursor(db);
  const query = since === undefined ? "" : `?since=${since}`;
  let response: Response;
  try {
    response = await fetcher(`/api/changes${query}`, {
      credentials: "same-origin",
      // The day-bound fields (`overdue`, `featured`, a habit's state) for the person's day (D22).
      headers: { "X-Time-Zone": Intl.DateTimeFormat().resolvedOptions().timeZone },
    });
  } catch {
    return null;
  }
  if (!response.ok) return null;
  const changes = (await response.json()) as Changes;
  await apply(db, changes);
  return { reset: changes.reset };
}
